import React from 'react';
import { ShoppingBag, Package, Heart, TrendingUp, Tag, Users, Headphones } from 'lucide-react';
import Navbar from './components/ui/navbar';

const CustomerPage: React.FC = () => {
  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "/create-space" },
    },
  };

  return (
    <div className="scroll-snap-container">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 px-4 pointer-events-none animate-navbar-float">
        <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[40px] px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <Navbar {...navbarConfig} />
        </div>
      </div>

      {/* Customer Options Section */}
      <section className="scroll-snap-section flex items-center justify-center">
        <div className="page-content-wrapper bg-white relative w-full flex items-center justify-center min-h-screen pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-6 text-center text-black">
              Welcome to Your Shopping Experience
            </h1>
            <p className="text-base sm:text-lg font-light max-w-3xl mx-auto mb-16 text-center text-gray-700">
              Choose how you want to explore - create your personalized space or browse our curated products
            </p>

            {/* Two-Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-6xl mx-auto">
              {/* Left Card - Create Space */}
              <div className="bg-white border-2 border-black rounded-2xl p-5 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-3">
                  <Package className="text-white" size={26} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold mb-2 text-black">
                  Create Your Space
                </h2>
                <p className="text-sm text-gray-700 mb-3 max-w-md leading-snug">
                  Build your personalized shopping experience. Curate collections, save favorites, and create a space that reflects your style.
                </p>
                <ul className="text-left mb-4 space-y-1.5 text-gray-700 text-sm">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Personalized recommendations
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Save and organize favorites
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Track orders and wishlist
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Connect with creators
                  </li>
                </ul>
                <button
                  onClick={() => window.location.href = '/create-space'}
                  className="bg-black text-white px-6 py-2.5 rounded-full hover:bg-gray-800 transition-colors font-semibold text-sm w-full max-w-sm"
                >
                  Create Your Space
                </button>
              </div>

              {/* Right Card - Product Page */}
              <div className="bg-white border-2 border-black rounded-2xl p-5 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-3">
                  <ShoppingBag className="text-white" size={26} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold mb-2 text-black">
                  Browse Products
                </h2>
                <p className="text-sm text-gray-700 mb-3 max-w-md leading-snug">
                  Explore our curated collection of unique items from NYC's most talented creators. Discover something special today.
                </p>
                <ul className="text-left mb-4 space-y-1.5 text-gray-700 text-sm">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Curated verified sellers
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Support independent artists
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Exclusive limited editions
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-2 flex-shrink-0"></span>
                    Fast secure checkout
                  </li>
                </ul>
                <button
                  onClick={() => window.location.href = '/products'}
                  className="bg-black text-white px-6 py-2.5 rounded-full hover:bg-gray-800 transition-colors font-semibold text-sm w-full max-w-sm"
                >
                  View All Products
                </button>
              </div>
            </div>

            {/* Additional Features Section */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              <div className="text-center bg-gray-50 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mb-2.5 mx-auto">
                  <Heart className="text-white" size={20} />
                </div>
                <h3 className="text-base font-bold mb-1 text-black">Support Artists</h3>
                <p className="text-gray-600 text-xs leading-snug">Every purchase directly supports independent creators</p>
              </div>

              <div className="text-center bg-gray-50 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mb-2.5 mx-auto">
                  <Package className="text-white" size={20} />
                </div>
                <h3 className="text-base font-bold mb-1 text-black">Fast Shipping</h3>
                <p className="text-gray-600 text-xs leading-snug">Quick delivery from local sellers near you</p>
              </div>

              <div className="text-center bg-gray-50 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mb-2.5 mx-auto">
                  <TrendingUp className="text-white" size={20} />
                </div>
                <h3 className="text-base font-bold mb-1 text-black">Quality Guaranteed</h3>
                <p className="text-gray-600 text-xs leading-snug">All items are verified and quality-checked</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerPage;
